import React, { useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaBolt,
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaRedo,
  FaSearch,
  FaTag,
} from "react-icons/fa";

const API_BASE_URL = (process.env.REACT_APP_API_URL || "https://tools.microters.com").replace(/\/$/, "");

const COLORS = {
  bg: "#FFF9F7",
  navy: "#1F2D4D",
  ink: "#171820",
  panel: "#FFFFFF",
  accent: "#FA5735",
  accentSoft: "#FFF0EB",
  green: "#16845B",
  muted: "#7C8497",
  border: "#DFE4EC",
};

const LANGUAGES = [
  ["en", "English"], ["bn", "বাংলা"], ["hi", "Hindi"], ["ur", "Urdu"],
  ["ar", "Arabic"], ["es", "Spanish"], ["fr", "French"], ["de", "German"],
  ["pt", "Portuguese"], ["tr", "Turkish"], ["id", "Indonesian"], ["ja", "Japanese"],
];

const COUNTRIES = [
  ["bd", "Bangladesh"], ["us", "United States"], ["gb", "United Kingdom"],
  ["in", "India"], ["ca", "Canada"], ["au", "Australia"], ["ae", "UAE"],
  ["sa", "Saudi Arabia"], ["de", "Germany"], ["fr", "France"], ["es", "Spain"],
  ["br", "Brazil"], ["jp", "Japan"], ["sg", "Singapore"],
];

const INTENT_COLORS = {
  Informational: ["#EAF1FE", "#2366D1"],
  Question: ["#F0EAFC", "#6E45C1"],
  Commercial: ["#FFF4D6", "#A76500"],
  Transactional: ["#E7F6EF", "#16845B"],
  Local: ["#FCE7DF", "#D94721"],
  Navigational: ["#EDF0F4", "#5D6677"],
};

const escapeCsv = (value) => `"${String(value == null ? "" : value).replace(/"/g, '""')}"`;
const formatNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0).toLocaleString();

const normalizeResult = (payload) => {
  const data = payload && typeof payload === "object" ? payload : {};
  const suggestions = Array.isArray(data.suggestions)
    ? data.suggestions
        .map((item, index) => typeof item === "string"
          ? { rank: index + 1, keyword: item, intent: "Informational", sourceQuery: data.seedKeyword || "" }
          : {
              rank: Number(item?.rank) || index + 1,
              keyword: item?.keyword || item?.value || "",
              intent: item?.intent || "Informational",
              sourceQuery: item?.sourceQuery || data.seedKeyword || "",
            })
        .filter((item) => item.keyword)
    : [];
  return {
    ...data,
    suggestions,
    totalSuggestions: Number.isFinite(Number(data.totalSuggestions)) ? Number(data.totalSuggestions) : suggestions.length,
    clusters: data.clusters && typeof data.clusters === "object" ? data.clusters : {},
    warnings: Array.isArray(data.warnings) ? data.warnings : [],
  };
};

const GoogleAutoSuggestKeywordTool = () => {
  const [keyword, setKeyword] = useState("");
  const [language, setLanguage] = useState("en");
  const [country, setCountry] = useState("bd");
  const [mode, setMode] = useState("quick");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("");
  const [activeIntent, setActiveIntent] = useState("All");

  const filteredSuggestions = useMemo(() => {
    if (!result) return [];
    const query = filter.trim().toLowerCase();
    return result.suggestions.filter((item) => {
      const intentMatches = activeIntent === "All" || item.intent === activeIntent;
      const textMatches = !query || `${item.keyword} ${item.intent} ${item.sourceQuery}`.toLowerCase().includes(query);
      return intentMatches && textMatches;
    });
  }, [result, filter, activeIntent]);

  const intents = useMemo(() => result ? ["All", ...Object.keys(result.clusters)] : ["All"], [result]);

  const explore = async (event) => {
    event.preventDefault();
    const seed = keyword.trim();
    if (seed.length < 2) {
      setError("Enter a seed keyword with at least 2 characters.");
      toast.error("Please enter a seed keyword.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    setFilter("");
    setActiveIntent("All");
    console.log("[GOOGLE SUGGEST UI] Request started.", { keyword: seed, language, country, mode });

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/google-suggest`,
        { keyword: seed, language, country, mode },
        { timeout: mode === "deep" ? 120000 : 30000 }
      );
      if (!response.data || response.data.success === false) throw new Error(response.data?.message || "Invalid API response.");
      const normalized = normalizeResult(response.data.data || response.data);
      setResult(normalized);
      console.log("[GOOGLE SUGGEST UI] Request completed.", {
        suggestions: normalized.totalSuggestions,
        clusters: normalized.clusters,
        cached: Boolean(normalized.cached),
        warnings: normalized.warnings.length,
      });
      if (normalized.suggestions.length) toast.success(`${formatNumber(normalized.totalSuggestions)} suggestion(s) found!`, { theme: "colored" });
      else toast.warning("Google returned no suggestions for this seed and region.");
    } catch (requestError) {
      const message = requestError.response?.data?.message || (requestError.code === "ECONNABORTED" ? "The request timed out." : requestError.message) || "Failed to fetch suggestions.";
      setError(message);
      console.error("[GOOGLE SUGGEST UI] Request failed:", requestError);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setKeyword("");
    setLanguage("en");
    setCountry("bd");
    setMode("quick");
    setLoading(false);
    setError("");
    setResult(null);
    setFilter("");
    setActiveIntent("All");
    console.log("[GOOGLE SUGGEST UI] Reset complete.");
    toast.info("Keyword tool reset.");
  };

  const copyKeywords = async () => {
    if (!filteredSuggestions.length) return;
    try {
      await navigator.clipboard.writeText(filteredSuggestions.map((item) => item.keyword).join("\n"));
      toast.success(`${formatNumber(filteredSuggestions.length)} keyword(s) copied!`, { theme: "colored" });
    } catch (copyError) {
      console.error("[GOOGLE SUGGEST UI] Copy failed:", copyError);
      toast.error("Failed to copy keywords.");
    }
  };

  const downloadCsv = () => {
    if (!filteredSuggestions.length) return;
    try {
      const rows = [["Rank", "Keyword", "Intent", "Source query"], ...filteredSuggestions.map((item) => [item.rank, item.keyword, item.intent, item.sourceQuery])];
      const csv = `\uFEFF${rows.map((row) => row.map(escapeCsv).join(",")).join("\r\n")}`;
      const objectUrl = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "google-auto-suggest-keywords.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
      console.log("[GOOGLE SUGGEST UI] CSV downloaded.", { rows: filteredSuggestions.length });
      toast.success("CSV downloaded!", { theme: "colored" });
    } catch (downloadError) {
      console.error("[GOOGLE SUGGEST UI] CSV download failed:", downloadError);
      toast.error("Failed to download CSV.");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, padding: "55px 16px" }}>
      <ToastContainer />
      <div className="container-fluid" >
       

        <div style={{ background: "rgba(255,255,255,0.62)", border: `1px solid ${COLORS.border}`, borderRadius: 22, padding: 32 }}>
          <form onSubmit={explore}>
            <div className="row g-2 align-items-end">
              <div className="col-lg-6">
                <label className="form-label small fw-bold"><FaTag color={COLORS.accent} /> Enter Seed Keyword</label>
                <input className="form-control" value={keyword} disabled={loading} maxLength={100} onChange={(event) => { setKeyword(event.target.value); setError(""); }} placeholder="e.g. digital marketing, keto diet..." style={{ height: 52 }} />
              </div>
              <div className="col-6 col-lg-2">
                <label className="form-label small fw-bold">Language</label>
                <select className="form-select" value={language} disabled={loading} onChange={(event) => setLanguage(event.target.value)} style={{ height: 52 }}>{LANGUAGES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
              </div>
              <div className="col-6 col-lg-2">
                <label className="form-label small fw-bold">Country</label>
                <select className="form-select" value={country} disabled={loading} onChange={(event) => setCountry(event.target.value)} style={{ height: 52 }}>{COUNTRIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
              </div>
              <div className="col-lg-2 d-grid">
                <button type="submit" disabled={loading} style={{ border: "none", borderRadius: 6, background: COLORS.accent, color: "#fff", height: 52, fontWeight: 800, boxShadow: "0 5px 12px rgba(250,87,53,0.22)" }}><FaSearch /> {loading ? "Loading…" : "Explore"}</button>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
              <div style={{ display: "flex", gap: 7 }}>
                <button type="button" onClick={() => setMode("quick")} className={`btn btn-sm ${mode === "quick" ? "btn-dark" : "btn-outline-secondary"}`}>Quick</button>
                <button type="button" onClick={() => setMode("deep")} className={`btn btn-sm ${mode === "deep" ? "btn-dark" : "btn-outline-secondary"}`}><FaBolt size={9} /> Deep explore</button>
              </div>
              <button type="button" onClick={reset} className="btn btn-sm btn-link text-secondary text-decoration-none"><FaRedo size={10} /> Reset</button>
            </div>
          </form>

          {error ? <div style={{ background: COLORS.accentSoft, color: COLORS.accent, borderRadius: 9, padding: "10px 12px", fontSize: 12.5, marginTop: 14 }}>{error}</div> : null}

          {!result && !loading ? (
            <div style={{ minHeight: 250, display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center", color: "#7D869A" }}>
              <div><div style={{ width: 80, height: 80, margin: "0 auto 16px", display: "grid", placeItems: "center", borderRadius: "50%", background: "#F3F5F8" }}><FaTag size={34} color="#D5DAE3" /></div><div style={{ fontSize: 16 }}>Start by entering a topic above.</div><div style={{ fontSize: 12, color: "#A0A8B9", marginTop: 5 }}>We will fetch keyword predictions and group them by intent.</div></div>
            </div>
          ) : null}

          {loading ? <div style={{ minHeight: 250, display: "flex", justifyContent: "center", alignItems: "center", textAlign: "center" }}><div><div className="spinner-border" style={{ color: COLORS.accent }} role="status" /><div style={{ color: COLORS.muted, marginTop: 13 }}>{mode === "deep" ? "Expanding multiple Google predictions…" : "Fetching Google predictions…"}</div></div></div> : null}

          {result ? (
            <div style={{ marginTop: 24 }}>
              <div className="row g-2 mb-3">
                <div className="col-md-3"><div style={{ background: "#F7F8FA", borderRadius: 10, padding: 12 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>SUGGESTIONS</div><strong style={{ fontSize: 20 }}>{formatNumber(result.totalSuggestions)}</strong></div></div>
                <div className="col-md-3"><div style={{ background: "#F7F8FA", borderRadius: 10, padding: 12 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>CLUSTERS</div><strong style={{ fontSize: 20 }}>{formatNumber(Object.keys(result.clusters).length)}</strong></div></div>
                <div className="col-md-3"><div style={{ background: "#F7F8FA", borderRadius: 10, padding: 12 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>QUERY MODE</div><strong style={{ fontSize: 20, textTransform: "capitalize" }}>{result.mode || mode}</strong></div></div>
                <div className="col-md-3"><div style={{ background: "#F7F8FA", borderRadius: 10, padding: 12 }}><div style={{ color: COLORS.muted, fontSize: 10 }}>SOURCE</div><strong style={{ fontSize: 15 }}>{result.cached ? "Cached" : "Live"}</strong></div></div>
              </div>

              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 13 }}>{intents.map((intent) => { const count = intent === "All" ? result.totalSuggestions : result.clusters[intent] || 0; return <button type="button" key={intent} onClick={() => setActiveIntent(intent)} className={`btn btn-sm ${activeIntent === intent ? "btn-dark" : "btn-outline-secondary"}`}>{intent} ({formatNumber(count)})</button>; })}</div>

              <div className="row g-2 mb-3">
                <div className="col"><input className="form-control form-control-sm" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Filter keywords or intent" /></div>
                <div className="col-auto"><button type="button" onClick={copyKeywords} disabled={!filteredSuggestions.length} className="btn btn-sm btn-outline-secondary"><FaCopy size={10} /> Copy</button></div>
                <div className="col-auto"><button type="button" onClick={downloadCsv} disabled={!filteredSuggestions.length} className="btn btn-sm" style={{ background: COLORS.accent, color: "#fff" }}><FaDownload size={10} /> CSV</button></div>
              </div>

              {result.warnings.length ? <div style={{ background: "#FFF5D9", color: "#8A6515", borderRadius: 8, padding: "9px 11px", fontSize: 11.5, marginBottom: 12 }}>{result.warnings.slice(0, 4).map((warning) => <div key={warning}>• {warning}</div>)}</div> : null}

              {filteredSuggestions.length ? (
                <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, maxHeight: 440, overflow: "auto" }}>
                  <table className="table table-hover table-sm mb-0" style={{ fontSize: 12 }}>
                    <thead style={{ position: "sticky", top: 0, background: "#F7F8FA", zIndex: 1 }}><tr><th style={{ width: 65 }}>Rank</th><th>Keyword</th><th style={{ width: 140 }}>Intent</th><th>Source query</th></tr></thead>
                    <tbody>{filteredSuggestions.map((item) => { const [background, color] = INTENT_COLORS[item.intent] || INTENT_COLORS.Informational; return <tr key={`${item.rank}-${item.keyword}`}><td>{item.rank}</td><td style={{ fontWeight: 700 }}>{item.keyword}</td><td><span style={{ display: "inline-block", background, color, padding: "4px 7px", borderRadius: 999, fontSize: 10, fontWeight: 800 }}>{item.intent}</span></td><td style={{ color: COLORS.muted }}>{item.sourceQuery}</td></tr>; })}</tbody>
                  </table>
                </div>
              ) : <div style={{ textAlign: "center", color: COLORS.muted, padding: 40 }}>No keyword matches this filter.</div>}
              <div style={{ color: COLORS.green, fontSize: 11.5, marginTop: 10 }}><FaCheckCircle size={10} /> Showing {formatNumber(filteredSuggestions.length)} keyword(s).</div>
            </div>
          ) : null}

          <div style={{ color: COLORS.muted, fontSize: 10.5, lineHeight: 1.5, marginTop: 18 }}>
            Google predictions are dynamic and may vary by language, country, trends, and service availability. This tool is not affiliated with or endorsed by Google.
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoogleAutoSuggestKeywordTool;
